import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { OnboardingApplicationService } from '../tokens/onboarding-application.token';
import { API_PATHS } from '../config/api-paths';
import {
  OnboardingApplicationForm,
  LenderOnboardingForm,
  SaveFinancialsRequest,
  SaveFinancingRequestRequest,
  SaveAccountPreferenceRequest,
  SaveBankConnectivityRequest,
  SaveBeneficialOwnersRequest,
  SaveLenderBusinessDetailsRequest,
  RegulatorAuthorisationStatus,
  EdOnboardingForm,
  SaveEdBusinessDetailsRequest,
  SupplyChainRole,
  SubmitApplicationResponse,
  UploadedDocument,
  UboEntry,
} from '../models/onboarding.model';
import { LenderType } from '../models/lender-registration.model';
import { SUPPORTED_BANKS } from '../constants/banks';

/** Backend shape of GET /{applicationId}/beneficial-owners. */
interface BackendBeneficialOwnersDTO {
  beneficialOwners?: {
    fullName?: string;
    ownershipPercentage?: number | null;
    countryOfResidence?: string;
  }[];
}

// ── Backend DTO shapes (mirror embedded OnboardingApplicationFormDTO) ──────────

/**
 * Minimal shape of the aggregate form DTO returned by the embedded service.
 *
 * Only the fields the portal actually consumes are typed; the rest can arrive
 * as `unknown` and are ignored.  Field names match the Java camelCase getters
 * serialised by Jackson.
 */
interface BackendFormDTO {
  id?: number;
  applicationReference?: string | null;
  status?: string;
  currentStage?: string;    // OnboardingStage enum name, e.g. "STAGE_2"
  track?: number | null;    // 1 or 2 (derived by backend from applicationType)
  draftAutoCloseAt?: string | null;

  companyInfo?: {
    legalName?: string;
    cui?: string;
    tradeRegistryNumber?: string;
    legalForm?: string;   // LegalForm enum name, e.g. "SRL"
    naceCode?: string;
    street?: string;
    streetExtra?: string;
    city?: string;
    county?: string;
    country?: string;
    postalCode?: string;
    representativeFullName?: string;
    representativePosition?: string;
    incorporationDate?: string | null;
    primaryEmail?: string | null;
    primaryPhone?: string | null;
  };

  financials?: {
    revenueLastFy?: number | null;
    revenuePriorFy?: number | null;
    numberOfEmployees?: number | null;
    employeeCountBand?: string | null;
    yearsInOperation?: number | null;
    expectedMonthlyVolume?: number | null;
    currency?: string;
  };

  financingRequest?: {
    product?: string | null;
    requestedAmount?: number | null;
    currency?: string;
    financingPurpose?: string | null;
    financingPurposeOther?: string;
    desiredTenorMonths?: number | null;
    urgency?: string | null;
  } | null;

  accountPreference?: {
    preferredAccountType?: string | null;
    cardProduct?: string | null;
    accountCurrencies?: string;  // backend: comma-separated string
    nameOnCard?: string;
    videoKycPreference?: string | null;
  } | null;

  /**
   * Psd2ConnectionState enum name from the backend.
   * The backend has no `bankConnectivity` section in the aggregate DTO — the
   * portal derives a synthetic `bankConnectivity` block from this field plus
   * `psd2SelectedBankCode`.
   */
  psd2ConnectionState?: string | null;

  /**
   * EM-1815 — the code of the bank connected on the PSD2 branch (e.g. `"BCR"`), read back by the
   * backend from the per-bank tag on the party's PSD2 AIS consent. `psd2ConnectionState` carries
   * only the state, so without this the reloaded wizard could say "connected" but never say to
   * WHICH bank, and every bank tile came back unselected.
   */
  psd2SelectedBankCode?: string | null;

  documents?: BackendDocumentDTO[];

  // Lender-specific blocks (present only for LENDER_NBFI / LENDER_SRL_SA).
  isNbfi?: boolean;
  lenderType?: string | null;   // LenderType enum name: NBFI | SRL | SA
  lenderBusinessDetails?: {
    lenderType?: string | null;
    numberOfEmployees?: number | null;
    availableExposureAmount?: number | null;
    investmentFocus?: string | null;
    // EM-1802 — NBFI licence block (BRS §2.2.2 / §2.4.2).
    regulatorAuthorisationNumber?: string | null;
    regulatorAuthorisationStatus?: string | null;
    regulatorRenewalDate?: string | null;
    regulatorName?: string | null;
  } | null;

  // Enterprise-Distributor-specific blocks (present only for ED_SRL_SA).
  isEd?: boolean;
  supplyChainRole?: string | null;
  autoRetrievalGaps?: string[] | null;
  edBusinessDetails?: {
    supplyChainRole?: string | null;
    numberOfEmployees?: number | null;
    legalForm?: string | null;
    naceCode?: string | null;
    industrySector?: string | null;
    distributionFocus?: string | null;
  } | null;
}

interface BackendDocumentDTO {
  id?: number;
  documentType?: string;
  originalFilename?: string;
  uploadedAt?: string;
}

// ── Mapper: backend DTO → portal model ───────────────────────────────────────

/**
 * Maps the backend `OnboardingApplicationFormDTO` to the portal's
 * `OnboardingApplicationForm` model so component code remains unchanged.
 *
 * Differences handled here:
 * - `companyInfo.address` is synthesised from the backend's split address fields
 * - `companyInfo.representative` is assembled from `representativeFullName` +
 *   `representativePosition`
 * - `bankConnectivity` is derived from `psd2ConnectionState` (no full section
 *   returned by the backend; the portal reconstructs from state enum only)
 * - `accountPreference.accountCurrencies` is split from the backend's
 *   comma-separated string back to `string[]`
 * - `currentStage` is parsed from the enum name (e.g. "STAGE_2" → 2)
 * - `status` is coerced to the portal's `ApplicationStatus` union
 * - `documents` maps `originalFilename` → `fileName`, `id: number` → `id: string`
 * - `ubos` is not returned by the backend — preserved as empty array
 */
function mapBackendDtoToPortal(dto: BackendFormDTO): OnboardingApplicationForm {
  const ci = dto.companyInfo ?? {};

  const addressParts = [ci.street, ci.streetExtra, ci.city, ci.county, ci.postalCode]
    .filter(p => p && p.trim().length > 0);

  const companyInfo = {
    legalName: ci.legalName ?? '',
    cui: ci.cui ?? '',
    tradeRegistryNumber: ci.tradeRegistryNumber ?? '',
    legalForm: ci.legalForm ?? '',
    naceCode: ci.naceCode ?? '',
    address: addressParts.join(', '),
    representative: [ci.representativeFullName, ci.representativePosition]
      .filter(Boolean).join(' — '),
    incorporationDate: ci.incorporationDate ?? '',
    primaryEmail: ci.primaryEmail ?? null,
    primaryPhone: ci.primaryPhone ?? null,
  };

  const fin = dto.financials ?? {};
  const financials = {
    revenueLastFy: fin.revenueLastFy ?? null,
    revenuePriorFy: fin.revenuePriorFy ?? null,
    employeeCountBand: (fin.employeeCountBand ?? null) as any,
    numberOfEmployees: fin.numberOfEmployees ?? null,
    yearsInOperation: fin.yearsInOperation ?? null,
    expectedMonthlyVolume: fin.expectedMonthlyVolume ?? null,
    currency: (fin.currency ?? 'RON') as 'RON' | 'EUR',
  };

  const fr = dto.financingRequest;
  const financingRequest = fr
    ? {
        product: (fr.product ?? null) as any,
        requestedAmount: fr.requestedAmount ?? null,
        currency: (fr.currency ?? 'RON') as 'RON' | 'EUR',
        financingPurpose: (fr.financingPurpose ?? null) as any,
        financingPurposeOther: fr.financingPurposeOther ?? '',
        desiredTenorMonths: fr.desiredTenorMonths ?? null,
        urgency: (fr.urgency ?? null) as any,
      }
    : null;

  const ap = dto.accountPreference;
  const accountPreference = ap
    ? {
        preferredAccountType: (ap.preferredAccountType ?? null) as any,
        cardProduct: (ap.cardProduct ?? null) as any,
        // EM-1813 — an EMPTY list, never a fabricated default. `ap` being present means a record
        // EXISTS, so an empty `accountCurrencies` is the record truthfully saying it holds no
        // currency; inventing `['RON']` here is precisely the "wizard shows RON ticked while the
        // record stores no currency at all" defect, and it sat UPSTREAM of the wizard, so the
        // Track-2 restore could never observe the empty case on the real REST path. The
        // grace-config default belongs to the no-record case (`ap == null`), which returns null
        // below and lets the page pre-tick it.
        accountCurrencies: ap.accountCurrencies
          ? ap.accountCurrencies.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        nameOnCard: ap.nameOnCard ?? '',
        videoKycPreference: (ap.videoKycPreference ?? null) as any,
      }
    : null;

  const documents: UploadedDocument[] = (dto.documents ?? []).map(d => ({
    id: String(d.id ?? ''),
    documentType: d.documentType ?? '',
    fileName: d.originalFilename ?? '',
    uploadedAt: d.uploadedAt ?? '',
  }));

  // Derive a bankConnectivity section from psd2ConnectionState + psd2SelectedBankCode.
  //
  // EM-1800 — `documents` is resolved FIRST and passed in: both non-PSD2 branches are stored as
  // the same `PROCEED_WITHOUT`, so a bank statement on file is the only thing that tells them
  // apart. Without it an SME who chose "Bank Statement Upload" reopened the wizard on the
  // "No Bank Connection" branch, with the two warnings pre-ticked and no statement recognised.
  const psd2State = dto.psd2ConnectionState ?? null;
  const bankConnectivity = deriveBankConnectivity(psd2State, documents, dto.psd2SelectedBankCode);

  // Parse currentStage number from enum name (e.g. "STAGE_2" → 2).
  const stageMatch = (dto.currentStage ?? '').match(/(\d+)$/);
  const currentStage = stageMatch ? parseInt(stageMatch[1], 10) : 2;

  // Coerce OnboardingStatus to the portal's ApplicationStatus union.
  const status = mapStatus(dto.status ?? '');

  return {
    id: dto.id ?? null,
    applicationReference: dto.applicationReference ?? null,
    status,
    currentStage,
    track: (dto.track ?? 1) as 1 | 2,
    draftAutoCloseAt: dto.draftAutoCloseAt ?? null,
    companyInfo,
    financials,
    financingRequest,
    accountPreference,
    bankConnectivity,
    documents,
    ubos: [],  // UBOs not in the aggregate form DTO; managed separately
  };
}

/**
 * Every `psd2ConnectionState` that means "the applicant went down the Open-Banking branch and the
 * connection was recorded".
 *
 * <p>EM-1800 — this used to be `AISP_DATA_RETRIEVED` alone, which stopped being the normal outcome
 * when EM-1685 made the backend MEASURE the AIS feed instead of asserting it: a bank that returns
 * accounts but no booked transactions lands on `PARTIAL`, and one that returns nothing readable
 * (or a psd2 outage) lands on `SCA_COMPLETE` — both with the AIS and PIS consents genuinely
 * captured. Neither mapped to the PSD2 branch, so a lender who had connected their bank re-opened
 * the wizard with NO branch selected (or, for `PARTIAL`, with "Bank Statement Upload" selected and
 * no statement on file), the section read "Incomplete" for ever, and Submit could never pass its
 * gate. `VALIDATED_READY_FOR_SCORING` is the same branch one step further along (BRS §3.6).
 */
const PSD2_BRANCH_STATES = ['SCA_COMPLETE', 'AISP_DATA_RETRIEVED', 'PARTIAL', 'VALIDATED_READY_FOR_SCORING'];

/** States meaning "proceeding without a bank connection" — incl. the officer-approved variant. */
const PROCEED_WITHOUT_STATES = ['PROCEED_WITHOUT', 'PROCEED_WITHOUT_REDUCED_CONFIDENCE'];

/**
 * Reconstruct the connectivity branch from the persisted `psd2ConnectionState`.
 *
 * <p>EM-1208 — `STATEMENT_UPLOAD` and `NO_CONNECTION` are both stored as `PROCEED_WITHOUT`,
 * so the presence of a `BANK_STATEMENT` document is the only thing that tells them apart on
 * reload. Callers that have the document list pass it in; without it the old, conservative
 * mapping is kept.
 *
 * <p>An in-flight (`IN_PROGRESS`) or failed (`FAILED`, `DECLINED`,
 * `FAILED_PENDING_MANUAL_RESOLUTION`) attempt deliberately maps to `null`: no branch is satisfied,
 * so the wizard shows the three options again rather than claiming a connection that is not there.
 */
function deriveBankBranch(psd2State: string | null, documents?: UploadedDocument[]): any {
  if (!psd2State) return null;
  if (PSD2_BRANCH_STATES.includes(psd2State)) return 'PSD2';
  if (PROCEED_WITHOUT_STATES.includes(psd2State)) {
    return documents?.some(d => d.documentType === 'BANK_STATEMENT')
      ? 'STATEMENT_UPLOAD'
      : 'NO_CONNECTION';
  }
  if (psd2State === 'ALTERNATIVE_STATEMENT_UNDER_REVIEW') return 'STATEMENT_UPLOAD';
  return null;
}

/** True when the persisted state means both PSD2 consents were captured (they are mandatory). */
function psd2ConsentsCaptured(psd2State: string | null): boolean {
  return psd2State !== null && PSD2_BRANCH_STATES.includes(psd2State);
}

/**
 * True when the APPLICANT acknowledged proceeding without bank connectivity.
 *
 * <p>Deliberately narrower than {@link PROCEED_WITHOUT_STATES}: `PROCEED_WITHOUT_REDUCED_CONFIDENCE`
 * is written by a KYC officer resolving Stage 3, never by the applicant, so reporting it as an
 * acknowledgement would claim — right above the "correct and complete" attestation — that the
 * applicant ticked two BRS §2.3.3 5C-III warnings they were never shown.
 */
function noConnectionAcknowledged(psd2State: string | null): boolean {
  return psd2State === 'PROCEED_WITHOUT';
}

/**
 * The synthetic `bankConnectivity` block every wizard restores Section 4 from — SME Track 1 / 2,
 * lender Stage 2 and E-D Stage 2 alike.
 *
 * <p>EM-1815 — one derivation, not three near-identical copies (Rule 17). The three mappers below
 * each held their own inline version, each hard-coding `selectedBankCode: null`, which is why a
 * reloaded draft came back on the right branch with the right green confirmation line and no bank
 * tile selected: the bank was simply not in the read model. It now is (`psd2SelectedBankCode`), and
 * the display name is resolved from the portal's supported-bank catalogue — the backend has no
 * bank-name table, and `SUPPORTED_BANKS` is what renders the tiles in the first place.
 *
 * <p>The bank is reported ONLY on the PSD2 branch. The backend answers with the bank on file for the
 * party, which outlives a later switch to "upload statements" / "no connection" (revoking a consent
 * does not delete it), so gating on the derived branch is what stops a stale bank being re-selected
 * on a branch that has none.
 */
function deriveBankConnectivity(
  psd2State: string | null,
  documents: UploadedDocument[],
  selectedBankCode: string | null | undefined,
) {
  const branch = deriveBankBranch(psd2State, documents);
  const bank = branch === 'PSD2' && selectedBankCode
    ? SUPPORTED_BANKS.find(b => b.code === selectedBankCode) ?? null
    : null;
  return {
    branch,
    selectedBankCode: branch === 'PSD2' ? selectedBankCode ?? null : null,
    selectedBankName: bank?.name ?? null,
    aisConsentGranted: psd2ConsentsCaptured(psd2State),
    pisConsentGranted: psd2ConsentsCaptured(psd2State),
    noConnectionAcknowledged: noConnectionAcknowledged(psd2State),
  };
}

/**
 * Coerce the backend `OnboardingStatus` (70+ values) to the portal's coarse four-value
 * `ApplicationStatus`.
 *
 * <p>EM-1169 — the fallback used to be `default: return 'DRAFT'`, which was actively wrong and
 * is the second half of the reported defect. `OnboardingStatus` only lists a handful of
 * pre-submission values; every other value means the dossier has been submitted and is
 * somewhere in the Stage 3–8 pipeline. Because the SME Flow-A automation advances a freshly
 * submitted application straight past `SUBMITTED_PENDING_REVIEW` (a submitted Track-2 dossier
 * sits at `PENDING_FINANCIAL_DATA_INPUT` within a second — verified live), that `default`
 * reported the application back to the wizard as an editable **DRAFT**.
 *
 * <p>The mapping is therefore inverted: only the explicitly pre-submission statuses (and an
 * absent status, which must never lock a user out) map to `'DRAFT'`; everything not otherwise
 * classified maps to `'SUBMITTED'`. That is what makes {@link isApplicantEditable} tell the
 * truth for every wizard.
 */
function mapStatus(status: string): any {
  switch (status) {
    // ── Pre-submission: the applicant still owns the dossier ──────────────────
    case '':                  // status absent from the DTO — never lock on missing data
    case 'DRAFT':
    // The E-D entry chain — created at PENDING_ACTIVATION, flipping
    // PENDING_ACTIVATION -> ACCOUNT_ACTIVATED -> STAGE2_READY on activation
    // (EdRegistrationServiceImpl). All three are pre-submission. Mirrors the backend's
    // ApplicationOwnershipHelper.APPLICANT_EDITABLE_STATUSES and sme-account's DRAFT_STATUSES.
    case 'PENDING_ACTIVATION':
    case 'ACCOUNT_ACTIVATED':
    case 'STAGE2_READY':
      return 'DRAFT';

    // ── Terminal outcomes ─────────────────────────────────────────────────────
    case 'APPLICATION_APPROVED': return 'APPROVED';
    case 'APPLICATION_REJECTED': return 'REJECTED';

    // ── Everything else means "submitted and with us" ──────────────────────────
    // (SUBMITTED_PENDING_REVIEW, PENDING_/PASSED_COMPLETENESS, PENDING_APPLICANT_MODIFICATION,
    //  MODIFIED_BY_APPLICANT, CONTACTED_BY_CS, and the whole Stage 4–8 pipeline.)
    default:
      return 'SUBMITTED';
  }
}

// ── Lender mapper: backend DTO → lender portal model ──────────────────────────

/**
 * Maps the backend `OnboardingApplicationFormDTO` (lender variant) to the
 * portal's `LenderOnboardingForm`. Reuses the shared company-info / bank-
 * connectivity / documents derivation; adds the lender business-details block.
 */
function mapBackendDtoToLender(dto: BackendFormDTO): LenderOnboardingForm {
  const ci = dto.companyInfo ?? {};

  const addressParts = [ci.street, ci.streetExtra, ci.city, ci.county, ci.postalCode]
    .filter(p => p && p.trim().length > 0);

  const companyInfo = {
    legalName: ci.legalName ?? '',
    cui: ci.cui ?? '',
    tradeRegistryNumber: ci.tradeRegistryNumber ?? '',
    legalForm: ci.legalForm ?? '',
    naceCode: ci.naceCode ?? '',
    address: addressParts.join(', '),
    representative: [ci.representativeFullName, ci.representativePosition]
      .filter(Boolean).join(' — '),
    incorporationDate: ci.incorporationDate ?? '',
    primaryEmail: ci.primaryEmail ?? null,
    primaryPhone: ci.primaryPhone ?? null,
  };

  const documents: UploadedDocument[] = (dto.documents ?? []).map(d => ({
    id: String(d.id ?? ''),
    documentType: d.documentType ?? '',
    fileName: d.originalFilename ?? '',
    uploadedAt: d.uploadedAt ?? '',
  }));

  // EM-1208 — the backend collapses both non-PSD2 branches onto PROCEED_WITHOUT, so a
  // bank statement on file is what distinguishes "Upload Bank Statements" from
  // "No Bank Connection" when the wizard is reopened.
  const psd2State = dto.psd2ConnectionState ?? null;
  const bankConnectivity = deriveBankConnectivity(psd2State, documents, dto.psd2SelectedBankCode);

  const lbd = dto.lenderBusinessDetails ?? {};
  const lenderType = (dto.lenderType ?? lbd.lenderType ?? null) as LenderType | null;

  const stageMatch = (dto.currentStage ?? '').match(/(\d+)$/);
  const currentStage = stageMatch ? parseInt(stageMatch[1], 10) : 2;

  return {
    id: dto.id ?? null,
    applicationReference: dto.applicationReference ?? null,
    status: mapStatus(dto.status ?? ''),
    currentStage,
    draftAutoCloseAt: dto.draftAutoCloseAt ?? null,
    companyInfo,
    isNbfi: dto.isNbfi ?? (lenderType === 'NBFI'),
    lenderType,
    lenderBusinessDetails: {
      lenderType,
      numberOfEmployees: lbd.numberOfEmployees ?? null,
      availableExposureAmount: lbd.availableExposureAmount ?? null,
      investmentFocus: lbd.investmentFocus ?? null,
      // EM-1802 — the NBFI licence block. `regulatorName` is resolved by the backend from the
      // configurable per-jurisdiction map, so the wizard renders it read-only.
      regulatorAuthorisationNumber: lbd.regulatorAuthorisationNumber ?? null,
      regulatorAuthorisationStatus:
        (lbd.regulatorAuthorisationStatus ?? null) as RegulatorAuthorisationStatus | null,
      regulatorRenewalDate: lbd.regulatorRenewalDate ?? null,
      regulatorName: lbd.regulatorName ?? null,
    },
    bankConnectivity,
    documents,
  };
}

function mapBackendDtoToEd(dto: BackendFormDTO): EdOnboardingForm {
  const ci = dto.companyInfo ?? {};

  const addressParts = [ci.street, ci.streetExtra, ci.city, ci.county, ci.postalCode]
    .filter(p => p && p.trim().length > 0);

  const companyInfo = {
    legalName: ci.legalName ?? '',
    cui: ci.cui ?? '',
    tradeRegistryNumber: ci.tradeRegistryNumber ?? '',
    legalForm: ci.legalForm ?? '',
    naceCode: ci.naceCode ?? '',
    address: addressParts.join(', '),
    representative: [ci.representativeFullName, ci.representativePosition]
      .filter(Boolean).join(' — '),
    incorporationDate: ci.incorporationDate ?? '',
    primaryEmail: ci.primaryEmail ?? null,
    primaryPhone: ci.primaryPhone ?? null,
  };

  const documents: UploadedDocument[] = (dto.documents ?? []).map(d => ({
    id: String(d.id ?? ''),
    documentType: d.documentType ?? '',
    fileName: d.originalFilename ?? '',
    uploadedAt: d.uploadedAt ?? '',
  }));

  // EM-1800 — same as the SME/lender mappers: the statement-upload and no-connection branches
  // share one persisted state, so the document list is what distinguishes them on reload.
  const psd2State = dto.psd2ConnectionState ?? null;
  const bankConnectivity = deriveBankConnectivity(psd2State, documents, dto.psd2SelectedBankCode);

  const ebd = dto.edBusinessDetails ?? {};
  const supplyChainRole = (dto.supplyChainRole ?? ebd.supplyChainRole ?? null) as SupplyChainRole | null;

  const stageMatch = (dto.currentStage ?? '').match(/(\d+)$/);
  const currentStage = stageMatch ? parseInt(stageMatch[1], 10) : 2;

  return {
    id: dto.id ?? null,
    applicationReference: dto.applicationReference ?? null,
    status: mapStatus(dto.status ?? ''),
    currentStage,
    draftAutoCloseAt: dto.draftAutoCloseAt ?? null,
    companyInfo,
    edBusinessDetails: {
      supplyChainRole,
      numberOfEmployees: ebd.numberOfEmployees ?? null,
      legalForm: ebd.legalForm ?? ci.legalForm ?? null,
      naceCode: ebd.naceCode ?? ci.naceCode ?? null,
      industrySector: ebd.industrySector ?? null,
      distributionFocus: ebd.distributionFocus ?? null,
    },
    supplyChainRole,
    autoRetrievalGaps: dto.autoRetrievalGaps ?? [],
    bankConnectivity,
    documents,
  };
}

// ── REST service ──────────────────────────────────────────────────────────────

/**
 * REST-backed implementation of `OnboardingApplicationService`.
 *
 * All calls are authenticated via the portal's `authInterceptor` which
 * attaches the signed-in user's Keycloak bearer token automatically.
 * (This is NOT an anonymous call — the SME must be logged in at Stage 2.)
 *
 * Base path: `API_PATHS.ONBOARDING_APPLICATION`
 * routed through the gateway as `/services/embedded/api/onboarding-application`.
 *
 * Backend contract (ownership checked server-side; 403/404 on mismatch):
 * - `getResumableApplications()` → GET  /resumable                → List<DTO>
 * - `getApplication(id)`  → GET  /{id}                          → DTO
 * - `saveFinancials(id)`  → PUT  /{id}/financials                → DTO
 * - `saveFinancingRequest(id)` → PUT /{id}/financing-request     → DTO
 * - `saveAccountPreference(id)` → PUT /{id}/account-preference   → DTO
 *   (backend AccountPreferenceSectionDTO.accountCurrencies is comma-joined string;
 *    this service joins the portal's string[] before sending)
 * - `saveBankConnectivity(id)` → PUT /{id}/bank-connectivity     → DTO
 * - `saveBeneficialOwners(id)` → PUT /{id}/beneficial-owners     → DTO
 * - `uploadDocument(id)`  → POST /{id}/documents (multipart)     → BackendDocumentDTO
 * - `deleteDocument(id)`  → DELETE /{id}/documents/{docId}       → 204
 * - `submit(id)`          → POST /{id}/submit (no request body)  → DTO | 400
 */
@Injectable({ providedIn: 'root' })
export class OnboardingApplicationRestService implements OnboardingApplicationService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return API_PATHS.ONBOARDING_APPLICATION;
  }

  getResumableApplications(): Observable<OnboardingApplicationForm[]> {
    return this.http.get<BackendFormDTO[]>(`${this.base}/resumable`).pipe(
      map(dtos => dtos.map(dto => mapBackendDtoToPortal(dto))),
    );
  }

  getApplication(applicationId: number): Observable<OnboardingApplicationForm> {
    return this.http.get<BackendFormDTO>(`${this.base}/${applicationId}`).pipe(
      map(dto => mapBackendDtoToPortal(dto)),
    );
  }

  saveFinancials(
    applicationId: number,
    data: SaveFinancialsRequest,
  ): Observable<OnboardingApplicationForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/financials`,
      data,
    ).pipe(map(mapBackendDtoToPortal));
  }

  saveFinancingRequest(
    applicationId: number,
    data: SaveFinancingRequestRequest,
  ): Observable<OnboardingApplicationForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/financing-request`,
      data,
    ).pipe(map(mapBackendDtoToPortal));
  }

  saveAccountPreference(
    applicationId: number,
    data: SaveAccountPreferenceRequest,
  ): Observable<OnboardingApplicationForm> {
    // Backend AccountPreferenceSectionDTO.accountCurrencies is a String (comma-joined).
    const backendPayload = {
      ...data,
      accountCurrencies: Array.isArray(data.accountCurrencies)
        ? data.accountCurrencies.join(',')
        : data.accountCurrencies,
    };
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/account-preference`,
      backendPayload,
    ).pipe(map(mapBackendDtoToPortal));
  }

  saveBankConnectivity(
    applicationId: number,
    data: SaveBankConnectivityRequest,
  ): Observable<OnboardingApplicationForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/bank-connectivity`,
      data,
    ).pipe(map(mapBackendDtoToPortal));
  }

  saveBeneficialOwners(
    applicationId: number,
    data: SaveBeneficialOwnersRequest,
  ): Observable<OnboardingApplicationForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/beneficial-owners`,
      data,
    ).pipe(map(mapBackendDtoToPortal));
  }

  getBeneficialOwners(applicationId: number): Observable<UboEntry[]> {
    return this.http.get<BackendBeneficialOwnersDTO>(
      `${this.base}/${applicationId}/beneficial-owners`,
    ).pipe(
      map(dto => (dto.beneficialOwners ?? []).map(b => ({
        name: b.fullName ?? '',
        ownershipPercentage: b.ownershipPercentage ?? null,
        nationality: b.countryOfResidence ?? '',
      }))),
    );
  }

  uploadDocument(
    applicationId: number,
    documentType: string,
    file: File,
    supplementary = false,
  ): Observable<UploadedDocument> {
    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('file', file);
    if (supplementary) {
      // Tag voluntary E-D uploads so they surface as "Supplementary — E-D Provided"
      // in the KYC review dossier (BRS Ch.3 §3.2.4).
      formData.append('supplementary', 'true');
    }
    return this.http.post<BackendDocumentDTO>(
      `${this.base}/${applicationId}/documents`,
      formData,
    ).pipe(
      map(d => ({
        id: String(d.id ?? ''),
        documentType: d.documentType ?? documentType,
        fileName: d.originalFilename ?? file.name,
        uploadedAt: d.uploadedAt ?? new Date().toISOString(),
      })),
    );
  }

  deleteDocument(applicationId: number, documentId: string): Observable<void> {
    // Backend path variable is a Long (numeric id).
    return this.http.delete<void>(`${this.base}/${applicationId}/documents/${+documentId}`);
  }

  submit(applicationId: number, _confirmationChecked: boolean): Observable<SubmitApplicationResponse> {
    // Backend POST /{applicationId}/submit takes no request body.
    // The confirmationChecked flag is enforced by the UI modal only.
    return this.http.post<BackendFormDTO>(
      `${this.base}/${applicationId}/submit`,
      null,
    ).pipe(
      map(dto => {
        const form = mapBackendDtoToPortal(dto);
        return {
          applicationReference: form.applicationReference ?? '',
          status: form.status,
          message: 'Application submitted successfully',
        } satisfies SubmitApplicationResponse;
      }),
    );
  }

  // ── Lender Stage-2 (BRS §2.2) — reuse the same endpoints, lender-aware view ──

  getLenderApplication(applicationId: number): Observable<LenderOnboardingForm> {
    return this.http.get<BackendFormDTO>(`${this.base}/${applicationId}`).pipe(
      map(mapBackendDtoToLender),
    );
  }

  saveLenderBusinessDetails(
    applicationId: number,
    data: SaveLenderBusinessDetailsRequest,
  ): Observable<LenderOnboardingForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/lender-business-details`,
      data,
    ).pipe(map(mapBackendDtoToLender));
  }

  saveLenderBankConnectivity(
    applicationId: number,
    data: SaveBankConnectivityRequest,
  ): Observable<LenderOnboardingForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/bank-connectivity`,
      data,
    ).pipe(map(mapBackendDtoToLender));
  }

  submitLender(applicationId: number): Observable<LenderOnboardingForm> {
    return this.http.post<BackendFormDTO>(
      `${this.base}/${applicationId}/submit`,
      null,
    ).pipe(map(mapBackendDtoToLender));
  }

  // ── Enterprise-Distributor Stage-2 (BRS Ch.3 §3.2) — reuse the same endpoints, E-D view ──

  getEdApplication(applicationId: number): Observable<EdOnboardingForm> {
    return this.http.get<BackendFormDTO>(`${this.base}/${applicationId}`).pipe(
      map(mapBackendDtoToEd),
    );
  }

  saveEdBusinessDetails(
    applicationId: number,
    data: SaveEdBusinessDetailsRequest,
  ): Observable<EdOnboardingForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/ed-business-details`,
      data,
    ).pipe(map(mapBackendDtoToEd));
  }

  saveEdBankConnectivity(
    applicationId: number,
    data: SaveBankConnectivityRequest,
  ): Observable<EdOnboardingForm> {
    return this.http.put<BackendFormDTO>(
      `${this.base}/${applicationId}/bank-connectivity`,
      data,
    ).pipe(map(mapBackendDtoToEd));
  }

  submitEd(applicationId: number): Observable<EdOnboardingForm> {
    return this.http.post<BackendFormDTO>(
      `${this.base}/${applicationId}/submit`,
      null,
    ).pipe(map(mapBackendDtoToEd));
  }
}
